
import { dataAccess } from './dataAccess';
import type { Notification } from '../types';
import { query, ref, orderByChild, equalTo } from 'firebase/database';
import { db } from './firebase';

type Unsubscribe = () => void;

/**
 * Subscribes to a user's notifications in real-time.
 */
export const subscribeToNotifications = (userId: string, callback: (notifications: Notification[]) => void): Unsubscribe => {
    const notificationsQuery = query(ref(db, 'notifications'), orderByChild('userId'), equalTo(userId));
    
    return dataAccess.subscribeToQuery(notificationsQuery, (data) => {
        const notifications: Notification[] = [];
        if (data) {
            Object.keys(data).forEach(key => {
                const notif = data[key];
                notifications.push({
                    id: key,
                    ...notif
                });
            });
        }
        // Sort by scheduledAt desc
        notifications.sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());
        callback(notifications);
    }, (error) => {
        console.error("Error subscribing to notifications:", error);
    });
};

/**
 * Marks a single notification as read.
 */
export const markNotificationAsRead = async (userId: string, notificationId: string): Promise<void> => {
    await dataAccess.markNotificationRead(notificationId);
};

/**
 * Marks all unread notifications as read for a given user.
 */
export const markAllNotificationsAsRead = async (userId: string, notifications: Notification[]): Promise<void> => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length === 0) return;

    await Promise.all(unreadNotifications.map(n => dataAccess.markNotificationRead(n.id)));
};

/**
 * Deletes a single notification.
 */
export const deleteNotification = async (userId: string, notificationId: string): Promise<void> => {
    await dataAccess.deleteNotification(notificationId);
};

/**
 * Clears all notifications for a specific user.
 */
export const clearNotifications = async (userId: string): Promise<void> => {
    await dataAccess.clearNotifications(userId);
};

/**
 * Creates a new notification for a specific user.
 */
export const createNotification = async (
    userId: string, 
    notificationData: Omit<Notification, 'id' | 'userId' | 'read' | 'scheduledAt'>
): Promise<void> => {
    await dataAccess.sendNotification(userId, notificationData);
};
